<template>
  <component :is="componentType" v-if="shouldShowModal" :target="target">
    <SfModal
      :align="align"
      :animated="animated"
      :content-padding="contentPadding"
      :close-on-backdrop-click="closeOnBackdropClick"
      :close-on-escape="closeOnEscape"
      :footer-align="footerAlign"
      :footer-padding="footerPadding"
      :hide-close="hideClose"
      :scrollable-content="scrollableContent"
      :size="size"
      :variant="variant"
      v-bind="attrs"
      @close="close"
      @close-attempt="handleCloseAttempt"
    >
      <template #title>
        <slot name="title" />
      </template>
      <template #messages>
        <slot name="messages" />
      </template>
      <template #body>
        <slot name="body" />
      </template>
      <template #messages-bottom>
        <slot name="messages-bottom" />
      </template>
      <template #footer="{ closeAfterAnimation }">
        <slot name="footer" :close-after-animation="closeAfterAnimation" />
      </template>
    </SfModal>
  </component>
</template>
<script setup lang="ts" generic="ID extends ModalPortalIdentifier">
import type {
  FooterAlign,
  ModalAlign,
  ModalSize,
  ModalVariant,
} from './constants'
import type { ModalPortalIdentifier, SilverfinEvents } from '@app/types/index'

import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  onUnmounted,
  useAttrs,
} from 'vue'

import SfPortal from '../sf-portal.vue'
import SfModal from './sf-modal.vue'
import { modalAlign, modalSizes, modalVariants } from './constants'
import useSetCSSVariable from '@app/shared/vue/composables/use-set-css-variable'

defineOptions({
  name: 'SfModalPortal',
  inheritAttrs: false,
})

interface Props {
  align?: ModalAlign
  animated?: boolean
  contentPadding?: number | null
  closeOnBackdropClick?: boolean
  closeOnEscape?: boolean
  footerAlign?: FooterAlign
  footerPadding?: number | null
  eager?: boolean
  hideClose?: boolean
  modalId: ID
  distanceFromBottom?: number
  scrollableContent?: boolean
  shouldUsePortal?: boolean
  size?: ModalSize
  target?: string
  variant?: ModalVariant
}

const props = withDefaults(defineProps<Props>(), {
  align: modalAlign.center,
  contentPadding: null,
  closeOnBackdropClick: true,
  closeOnEscape: true,
  footerAlign: 'center',
  footerPadding: null,
  distanceFromBottom: 0,
  shouldUsePortal: true,
  size: modalSizes.xs,
  target: '#sf-modal-portal',
  variant: modalVariants.default,
})

const attrs = useAttrs()

const emit =
  defineEmits<{
    closeAttempt: []
    close: []
    opened: [SilverfinEvents[`${ID}-opened`]]
  }>()

const shouldShowModal = ref(false)

const componentType = computed(() => {
  return props.shouldUsePortal ? SfPortal : 'div'
})

const showContent = (payload: SilverfinEvents[`${ID}-opened`]) => {
  shouldShowModal.value = true
  emit('opened', payload)
}

const hideContent = () => {
  shouldShowModal.value = false
}

const close = () => {
  shouldShowModal.value = false
  window.modalCitizen.closeModalPortal()
  emit('close')
}

useSetCSSVariable('--safe-distance-for-modal-bottom', props.distanceFromBottom)

function handleCloseAttempt() {
  emit('closeAttempt')
}

onMounted(() => {
  window.eventHub.$on(`${props.modalId}-opened`, showContent)
  window.eventHub.$on(`${props.modalId}-closed`, hideContent)

  if (props.eager) {
    window.modalCitizen.openModalPortal(props.modalId)
  }
})

onBeforeUnmount(() => {
  window.eventHub.$off(`${props.modalId}-opened`, showContent)
  window.eventHub.$off(`${props.modalId}-closed`, hideContent)
})

onUnmounted(() => {
  useSetCSSVariable('--safe-distance-for-modal-bottom', 0)
})
</script>
